import { calculateMonthData, DayData, getVisibilityRating } from './calendarData';
import { eachDayOfInterval, parseISO, format, subDays } from 'date-fns';
import { coreDirectionLine, darkCoreLabel } from './coreDirection';

interface TripStop {
  id: string;
  location_name: string;
  latitude: number;
  longitude: number;
  arrival_date: string;
  departure_date: string;
  stop_order: number;
  notes: string | null;
}

interface Trip {
  name: string;
  stops: TripStop[];
}

function formatDateForICS(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

function formatDateTimeForICS(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}${m}${d}T${h}${min}${s}Z`;
}

function escapeICSText(text: string): string {
  return text.replace(/[,;\\]/g, '\\$&').replace(/\n/g, '\\n');
}

function getStarRating(rating: number): string {
  return '⭐'.repeat(Math.min(rating, 10)) || '☆';
}

function getNightDataForStop(stop: TripStop): Array<DayData & { displayDate: string }> {
  const arrival = parseISO(stop.arrival_date);
  const departure = parseISO(stop.departure_date);
  const lastNight = subDays(departure, 1);
  const nights = arrival <= lastNight ? eachDayOfInterval({ start: arrival, end: lastNight }) : [];

  const location = {
    name: stop.location_name,
    latitude: Number(stop.latitude),
    longitude: Number(stop.longitude),
  };

  return nights
    .map((night) => {
      const monthData = calculateMonthData(location, night.getMonth(), night.getFullYear());
      const dayData = monthData.find((d) => d.date === night.getDate());
      if (!dayData || dayData.moonPhase === undefined) return null;
      return {
        ...dayData,
        displayDate: format(night, 'MMM d, yyyy'),
      };
    })
    .filter(Boolean) as Array<DayData & { displayDate: string }>;
}

function createStopEvent(stop: TripStop, trip: Trip): string {
  const arrival = parseISO(stop.arrival_date);
  const departure = parseISO(stop.departure_date);
  // ICS DTEND for all-day events is exclusive, so add 1 day
  const endDate = new Date(departure);
  endDate.setDate(endDate.getDate() + 1);

  const nightData = getNightDataForStop(stop);
  const ratings = nightData.map((n) => getVisibilityRating(n));
  const bestRating = ratings.length > 0 ? Math.max(...ratings) : 0;
  const goodNights = ratings.filter((r) => r >= 4).length;
  const totalNights = nightData.length;

  let description =
    `📍 ${stop.location_name}` +
    `\\n📐 ${Number(stop.latitude).toFixed(4)}°, ${Number(stop.longitude).toFixed(4)}°` +
    `\\n\\n🌌 VISIBILITY SUMMARY` +
    `\\n${totalNights} night${totalNights !== 1 ? 's' : ''} total` +
    `\\n${goodNights} good night${goodNights !== 1 ? 's' : ''} (rating 4+)` +
    `\\nBest rating: ${bestRating}/10`;

  if (bestRating > 0) {
    const bestIdx = ratings.indexOf(bestRating);
    const bestNight = nightData[bestIdx];
    if (bestNight) {
      const moonPct = Math.round(bestNight.moonPhase * 100);
      description +=
        `\\n\\n🌟 BEST NIGHT: ${bestNight.displayDate}` +
        `\\nRating: ${getStarRating(bestRating)} (${bestRating}/10)` +
        `\\nMoon: ${moonPct}%` +
        `\\nCore: ${bestNight.galacticCoreStart || '--'} – ${bestNight.galacticCoreEnd || '--'}` +
        (darkCoreLabel(bestNight) ? `\\n${darkCoreLabel(bestNight)}` : '') +
        (coreDirectionLine(bestNight) ? `\\nPoint: ${coreDirectionLine(bestNight)}` : '');
    }
  }

  if (stop.notes) {
    description += `\\n\\n📝 NOTES: ${stop.notes}`;
  }

  description +=
    `\\n\\n📱 Plan details: https://www.milkywayplanner.com/dashboard?tab=trips` +
    `\\n\\nGenerated by MilkyWayPlanner.com`;

  // Alarm on first evening of the stop
  const alarmDate = new Date(arrival);
  alarmDate.setHours(18, 0, 0, 0);

  return [
    'BEGIN:VEVENT',
    `UID:trip-${stop.id}@milkywayplanner.com`,
    `DTSTAMP:${formatDateTimeForICS(new Date())}`,
    `CREATED:${formatDateTimeForICS(new Date())}`,
    `LAST-MODIFIED:${formatDateTimeForICS(new Date())}`,
    `DTSTART;VALUE=DATE:${formatDateForICS(arrival)}`,
    `DTEND;VALUE=DATE:${formatDateForICS(endDate)}`,
    `SUMMARY:${escapeICSText(`📍 Stop ${stop.stop_order}: ${stop.location_name}`)}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${escapeICSText(`${stop.location_name} (${Number(stop.latitude).toFixed(4)}°, ${Number(stop.longitude).toFixed(4)}°)`)}`,
    `GEO:${stop.latitude};${stop.longitude}`,
    'CATEGORIES:Astrophotography,Milky Way,Travel,Photography,Planning',
    'STATUS:CONFIRMED',
    'TRANSP:TRANSPARENT',
    'CLASS:PUBLIC',
    'SEQUENCE:0',
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapeICSText(`Trip stop: ${stop.location_name} — ${goodNights} good shooting night${goodNights !== 1 ? 's' : ''}`)}`,
    `TRIGGER;VALUE=DATE-TIME:${formatDateTimeForICS(alarmDate)}`,
    'END:VALARM',
    'END:VEVENT',
  ].join('\r\n');
}

/**
 * Generate an ICS file for a trip. Each stop becomes a calendar event
 * spanning arrival to departure, with visibility summary in the description.
 */
export function generateTripICS(
  trip: Trip
): { icsContent: string; eventCount: number; filename: string } {
  const sortedStops = [...trip.stops].sort((a, b) => a.stop_order - b.stop_order);
  const events = sortedStops.map((stop) => createStopEvent(stop, trip));

  const tripSlug = trip.name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  const calendarTitle = `${trip.name} — Milky Way Planner Trip`;

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//MilkyWayPlanner.com//Trip Planner V1.0//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeICSText(calendarTitle)}`,
    `X-WR-CALDESC:${escapeICSText(`Astrophotography trip itinerary with ${events.length} stop${events.length !== 1 ? 's' : ''} and visibility data`)}`,
    'X-WR-TIMEZONE:UTC',
    'BEGIN:VTIMEZONE',
    'TZID:UTC',
    'BEGIN:STANDARD',
    'DTSTART:19700101T000000',
    'TZOFFSETFROM:+0000',
    'TZOFFSETTO:+0000',
    'TZNAME:UTC',
    'END:STANDARD',
    'END:VTIMEZONE',
    ...events,
    'END:VCALENDAR',
  ].join('\r\n');

  return {
    icsContent,
    eventCount: events.length,
    filename: `trip_${tripSlug}_${format(new Date(), 'yyyy-MM-dd')}.ics`,
  };
}
